import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Element, scroller } from 'react-scroll';
import { updateDatesFilter } from '../mainFilter/mainFilterSlice';
import { selectDestinationGraph } from './priceGraphSlice';
import PriceGraphMobile from './PriceGraphMobile';
import PriceGraphDesktop from './PriceGraphDesktop';
import { pushEvent } from '../../utilities/analytics';
import { APPLY_PRICE_GRAPH_EVENT } from '../../constants/analyticEvents';
import { selectDealsList } from '../dealsList/dealsListSlice';
import PriceGraphPlaceholder from './PriceGraphPlaceholder';
import {
  selectActiveDestination,
  selectFilterData,
} from '../mainFilter/selectors';
import { addDays, differenceInCalendarDays } from 'date-fns';
import { updateSearch } from '../mainFilter/searchSlice';
import format from '../../utilities/format';
import { parseDate } from '../../utilities/helpers';
import usePriceGraph from './hooks/usePriceGraph';
import { clearPrefetchDeals } from '../dealsList/prefetchDealsSlice';

export default function PriceGraph({ handleShowFade }) {
  const dispatch = useDispatch();
  const mainFilter = useSelector(selectFilterData);
  const { pending: dealsPending } = useSelector(selectDealsList);
  const destination = useSelector(selectActiveDestination);
  const {
    who: { adults, children, childAges },
    when: { startDate, months, type, initial: initialWhenDates },
  } = mainFilter.searchEngine;
  const destinationGraph = useSelector(selectDestinationGraph);
  const [graphDateSelected, setGraphDateSelected] = useState(false);
  const [filterDate, setFilterDate] = useState({ month: 0, date: 1 });
  const currentDestination = destinationGraph.destination[destination.id];

  const { nightsLimit } = usePriceGraph();

  useEffect(() => {
    if (graphDateSelected && !dealsPending) {
      let timeout = setTimeout(() => {
        scroller.scrollTo('price-graph', {
          delay: 0,
          offset: -150,
          smooth: true,
          duration: 400,
        });
      }, 810);

      setGraphDateSelected(false);

      return () => {
        clearTimeout(timeout);
      }
    }

  }, [dealsPending, graphDateSelected]);

  useEffect(() => {
    if (type === 'month' && months && months.length > 0) {
      setFilterDate({ month: months[0], date: 1 });
    } else if (startDate) {
      const startDateArr = startDate.split('/');
      setFilterDate({ month: +startDateArr[1], date: +startDateArr[0] });
    }
  }, [mainFilter, months, startDate, type]);


  const applyDates = useCallback(({ start, end }) => {
    const date = {
      type: 'specific',
      startDate: format(start, 'dd/MM/yyyy'),
      endDate: format(end, 'dd/MM/yyyy'),
    };
    dispatch(clearPrefetchDeals());
    dispatch(updateDatesFilter(date));
    dispatch(updateSearch({ when: date }));
    pushEvent(APPLY_PRICE_GRAPH_EVENT, {
      date,
      destinationName: destination.name,
      destinationId: destination.id,
    });
    setGraphDateSelected(true);
    handleShowFade();
  }, [destination.id, destination.name, dispatch, handleShowFade]);

  const generateEmptyDates = useCallback(() => {
    if (!(currentDestination?.dates && currentDestination?.dates?.length) || !initialWhenDates) return;

    const graphRangeStart = parseDate(currentDestination.dates[0].start);
    const searchRangeStart = parseDate(initialWhenDates.specific.start);

    const itemsToAppend = differenceInCalendarDays(
      graphRangeStart,
      searchRangeStart
    );

    let emptyItems = [];
    for (let i = 1; i < itemsToAppend; i++) {
      const start = format(addDays(searchRangeStart, i), 'dd/MM/yyyy');
      emptyItems.push({
        start: start,
        end: start,
        avgPrice: 0,
      })
    }

    return emptyItems;
  }, [currentDestination, initialWhenDates])

  const mappedDates = useMemo(() => {
    const infants = childAges.filter((age) => age < 2).length;
    if (currentDestination?.dates && currentDestination?.dates?.length) {
      const realDates = currentDestination?.dates.map((date) => ({
        ...date,
        avgPrice: Math.round(date.avgPrice / (adults + (children - infants))),
      }))

      return [...generateEmptyDates(), ...realDates];
    }

  }, [adults, children, currentDestination?.dates, childAges, generateEmptyDates])

  if (!currentDestination) {
    return (
      <PriceGraphPlaceholder
        destination={destination}
        nights={nightsLimit.min}
      />
    );
  }

  if(!mappedDates) { return null; }

  return (
    <div className="c-row">
      <Element name="price-graph" />
      {window.innerWidth <= 768 ? (
        <PriceGraphMobile
          dates={mappedDates}
          applyDates={applyDates}
          nightsLimit={nightsLimit}
          destination={destination}
          filterDate={filterDate}
        />
      ) : (
        <PriceGraphDesktop
          dates={mappedDates}
          applyDates={applyDates}
          nightsLimit={nightsLimit}
          destination={destination}
          filterDate={filterDate}
        />
      )}
    </div>
  );
}
